import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass, DotsThreeVertical, UsersThree, Megaphone, Users, ChatCircleDots,
  ArrowClockwise, Gear, SignOut, ArrowLeft,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InstantAccessPT from './InstantAccessPT';
import InstantAccessG from './InstantAccessG';
import InstantAccessN from './InstantAccessN';
import InstantAccessPW from './InstantAccessPW';
import InquiryPanel from './InquiryPanel';
import { truncatePreview, truncateName } from '../utils/previewTruncate';
import MBSetupBadge from './MBSetupBadge';

/**
 * WhatsApp-style mobile view for Admin (super_admin) — renders only when the
 * user is on a phone / PWA. Desktop admin layout stays untouched.
 *
 * User-facing goals (Feb 9, 2026):
 *  • Big DWS logo (WhatsApp header vibe) + current section as subtitle
 *  • Compact search bar
 *  • Filter chips: All | Unread | Favourites | Groups   (no MB chips)
 *  • Horizontal swipe changes section (deposit ⇄ withdrawal ⇄ support ⇄ inquiry)
 *  • Bigger, WhatsApp-style chat rows
 *  • Three-dot menu on top-right for Settings / Broadcast / etc.
 *  • No bottom navigation
 *
 * Everything else (business logic, endpoints, state) stays in AdminDashboard.
 * We just receive the state as props.
 */
const SECTIONS = [
  { key: 'deposit', label: 'Deposit' },
  { key: 'withdrawal', label: 'Withdrawal' },
  { key: 'support', label: 'Support' },
  { key: 'inquiry', label: 'Inquiry' },
];

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'favourites', label: 'Favourites' },
  { key: 'groups', label: 'Groups' },
];

const AVATAR_PALETTE = [
  { bg: '#DCE7F5', fg: '#2E5AAC' }, // soft blue
  { bg: '#D6EDDA', fg: '#1F6E3A' }, // soft green
  { bg: '#F7DED0', fg: '#B14A16' }, // soft peach
  { bg: '#EBD9F0', fg: '#6B2E82' }, // soft purple
  { bg: '#FBE1E1', fg: '#B32828' }, // soft rose
  { bg: '#D9EEF2', fg: '#1F6B7C' }, // soft teal
  { bg: '#F4E6C7', fg: '#8B6914' }, // soft mustard
  { bg: '#E5E1F5', fg: '#3B3591' }, // soft indigo
];

import formatChatTime from '../utils/formatChatTime';

const MobileWhatsAppAdminView = ({
  activeTab, setActiveTab,
  filterTab, setFilterTab,
  filteredRooms, unreadCounts, rooms,
  searchQuery, setSearchQuery,
  handleChatClick, handleLogout,
  user, loadRooms, loadUnreadCounts, loadInquiryData,
  supportSubTab, setSupportSubTab,
  mbFilter, setMbFilter, mbOptions,
  unreadTabBadge, favouritesTabBadge, groupsTabBadge,
  // Selection mode props
  selectionMode, selectedRooms, cancelSelection,
  handleLongPressStart, handleLongPressEnd, handleTouchMove,
  toggleRoomSelection, setShowDeleteConfirm, showDeleteConfirm, handleDeleteRooms,
  handleToggleArchive, handleToggleFavourite,
  API,
}) => {
  const navigate = useNavigate();
  // Inquiry slide → shared InquiryPanel (filter + list) instead of chat list.
  const isInquirySection = activeTab === 'inquiry';
  const [showMenu, setShowMenu] = useState(false);
  const [mbOpen, setMbOpen] = useState(false);
  const [mbMenuPos, setMbMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const mbRef = useRef(null);
  const listRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Restore chat-list scroll position on mount (survives back-nav).
  // Runs when list mounts AND after rows are populated (deps include list length).
  useEffect(() => {
    if (!listRef.current) return;
    try {
      const saved = parseInt(sessionStorage.getItem(`admin_list_scroll_${activeTab}`) || '0', 10);
      if (saved > 0) {
        // Defer to next frame so the DOM has laid out the rows first.
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = saved;
        });
      }
    } catch (_) {}
  }, [activeTab, (filteredRooms || []).length]);

  const _openChat = (roomId) => {
    try {
      if (listRef.current) {
        sessionStorage.setItem(`admin_list_scroll_${activeTab}`, String(listRef.current.scrollTop));
      }
    } catch (_) {}
    handleChatClick(roomId);
  };

  // Point 2 — save the OUTGOING tab's scroll BEFORE switching tabs so each
  // Deposit/Withdraw/Support/Inquiry panel restores its own position (no bleed).
  const saveTabScroll = () => {
    try {
      if (listRef.current) sessionStorage.setItem(`admin_list_scroll_${activeTab}`, String(listRef.current.scrollTop));
    } catch (_) {}
  };

  // Close menu on outside click
  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    if (showMenu) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [showMenu]);

  // Close MB selector on outside click
  useEffect(() => {
    const onClick = (e) => { if (mbRef.current && !mbRef.current.contains(e.target)) setMbOpen(false); };
    if (mbOpen) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [mbOpen]);

  const currentIdx = SECTIONS.findIndex(s => s.key === activeTab);
  const currentSection = SECTIONS[currentIdx] || SECTIONS[0];

  const goToSection = (nextIdx) => {
    if (nextIdx < 0 || nextIdx >= SECTIONS.length) return;
    saveTabScroll();
    setActiveTab(SECTIONS[nextIdx].key);
  };

  // Horizontal swipe detection on the chat list area
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current || 0);
    touchStartX.current = null;
    touchStartY.current = null;
    // Only treat as horizontal swipe if X-move dominates and > 60px
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goToSection(currentIdx + 1); // left swipe → next
      else goToSection(currentIdx - 1);        // right swipe → prev
    }
  };

  const chatList = filteredRooms || [];
  const archivedCount = (rooms || []).filter(r => r.is_archived).length;

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden" data-testid="mobile-whatsapp-admin-view">
      {/* SELECTION MODE HEADER */}
      {selectionMode ? (
        <div className="bg-[#00a884] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={cancelSelection} className="p-1 hover:bg-white/10 rounded-full" data-testid="mobile-sel-cancel">
              <span className="text-2xl leading-none">×</span>
            </button>
            <span className="font-semibold">{selectedRooms.length} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleFavourite}
              disabled={selectedRooms.length === 0}
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm disabled:opacity-50"
              data-testid="mobile-sel-favourite"
            >
              ⭐ Fav
            </button>
            <button
              onClick={handleToggleArchive}
              disabled={selectedRooms.length === 0}
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm disabled:opacity-50"
              data-testid="mobile-sel-archive"
            >
              📦 {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={selectedRooms.length === 0}
              className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-sm disabled:opacity-50"
              data-testid="mobile-sel-delete"
            >
              Delete
            </button>
          </div>
        </div>
      ) : activeTab === 'archive' ? (
      /* ARCHIVE HEADER — WhatsApp style back arrow */
      <div className="bg-white flex items-center gap-3 px-4 py-3 flex-shrink-0 border-b border-gray-100">
        <button
          onClick={() => {
            let prev = 'deposit';
            try {
              const saved = sessionStorage.getItem('admin_tab_before_archive');
              if (saved && ['deposit','withdrawal','support','inquiry'].includes(saved)) prev = saved;
              sessionStorage.removeItem('admin_tab_before_archive');
            } catch (_) {}
            saveTabScroll();
            setActiveTab(prev);
          }}
          className="hover:bg-gray-100 p-1.5 rounded-full transition-colors"
          data-testid="mobile-archive-back-btn"
          aria-label="Back"
        >
          <span className="text-2xl leading-none">←</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xl">📦</span>
          <h2 className="font-semibold text-gray-900 text-lg">
            Archived <span className="text-sm text-gray-500 font-normal">({(rooms || []).filter(r => r.is_archived).length})</span>
          </h2>
        </div>
      </div>
      ) : (
      /* HEADER — WhatsApp style */
      <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00a884] to-[#008f70] flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-white font-black text-[14px] tracking-tight">DWS</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-[17px] font-bold text-gray-900 leading-tight" data-testid="mobile-brand">
                DWS <span className="text-[#00a884]">Support</span>
              </h1>
              <p className="text-[11px] text-gray-500 leading-tight" data-testid="mobile-section-label">
                {currentSection.label} panel
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <InstantAccessPW variant="light" />
            <InstantAccessN variant="light" />
            <InstantAccessPT variant="light" />
            <InstantAccessG variant="light" />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(v => !v)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
                data-testid="mobile-3dot-menu"
              >
                <DotsThreeVertical size={22} weight="bold" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-10 bg-white rounded-lg shadow-2xl border z-[100] w-56 py-1">
                  <button onClick={() => { setShowMenu(false); navigate('/groups'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <UsersThree size={18} /> Groups
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/broadcast'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Megaphone size={18} /> Broadcast
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/client-list'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Users size={18} /> Clients
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/admin/support-analytics'); }} className="w-full px-4 py-2.5 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2">
                    <ChatCircleDots size={18} /> Support Analytics
                  </button>
                  <button onClick={async () => { setShowMenu(false); toast.info('Refreshing...'); await loadRooms(); await loadUnreadCounts(); if (activeTab === 'inquiry' && loadInquiryData) await loadInquiryData(); toast.success('Refreshed!'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <ArrowClockwise size={18} /> Refresh
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/admin-settings'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Gear size={18} /> Settings
                  </button>
                  <div className="border-t my-1" />
                  <button onClick={() => { setShowMenu(false); handleLogout(); }} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <SignOut size={18} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search bar - WhatsApp style */}
        <div className="mt-2.5 relative">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
            className="w-full bg-gray-100 rounded-full pl-11 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none placeholder:text-gray-500"
            data-testid="mobile-search"
          />
        </div>
      </div>
      )}

      {/* SECTION PAGINATION INDICATOR (dots) — hidden in archive view */}
      {activeTab !== 'archive' && (
      <div className="flex items-center justify-center gap-1.5 py-1 flex-shrink-0" data-testid="section-dots">
        {SECTIONS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => goToSection(i)}
            className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-6 bg-[#00a884]' : 'w-1.5 bg-gray-300'}`}
            aria-label={`Go to ${s.label}`}
            data-testid={`section-dot-${s.key}`}
          />
        ))}
      </div>
      )}

      {/* FILTER CHIPS — All (with MB selector) / Unread / Favourites / Groups — hidden in archive + Inquiry */}
      {activeTab !== 'archive' && !isInquirySection && (
      <div className="px-3 pb-2 flex gap-2 overflow-x-auto flex-shrink-0 no-scrollbar" data-testid="mobile-filter-chips">
        {/* "All" chip acts as MB filter — tap to pick a specific MB or All */}
        <div className="relative flex-shrink-0" ref={mbRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Compute absolute-fixed dropdown position from the button rect so
              // it isn't clipped by the parent's `overflow-x-auto` wrapper.
              const rect = e.currentTarget.getBoundingClientRect();
              setMbMenuPos({ top: rect.bottom + 6, left: rect.left });
              setFilterTab('all');
              setMbOpen(v => !v);
            }}
            className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1 transition-colors ${
              filterTab === 'all'
                ? 'bg-[#d9fdd3] text-[#00a884] font-semibold'
                : 'text-gray-700 border border-gray-300'
            }`}
            data-testid="mobile-filter-all"
          >
            {mbFilter === 'all' ? 'All' : mbFilter}
            <span className="text-[10px]">▾</span>
          </button>
          {mbOpen && (
            <div
              className="fixed z-[80] bg-white rounded-xl shadow-2xl border border-gray-200 py-1 min-w-[140px] max-h-[70vh] overflow-y-auto"
              style={{ top: mbMenuPos.top, left: mbMenuPos.left }}
              data-testid="mobile-mb-menu"
            >
              {(mbOptions && mbOptions.length ? mbOptions : ['all','MB7','MB8','MB9','MB10','MB11']).map(opt => (
                <button
                  key={opt}
                  onClick={() => { setMbFilter(opt); setMbOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    mbFilter === opt ? 'text-[#00a884] font-semibold bg-[#d9fdd3]/40' : 'text-gray-700'
                  }`}
                  data-testid={`mobile-mb-opt-${opt}`}
                >
                  {opt === 'all' ? 'All MBs' : opt}
                </button>
              ))}
            </div>
          )}
        </div>
        {FILTERS.filter(f => f.key !== 'all').map(f => {
          const active = filterTab === f.key;
          // Point 60 — chip badges count unread CHATS (not messages), scoped
          // to the current section/MB filter by the parent (AdminDashboard).
          const badge = f.key === 'unread' ? unreadTabBadge
            : f.key === 'favourites' ? favouritesTabBadge
            : f.key === 'groups' ? groupsTabBadge
            : 0;
          return (
            <button
              key={f.key}
              onClick={() => f.key === 'groups' ? navigate('/groups') : setFilterTab(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                active
                  ? 'bg-[#d9fdd3] text-[#00a884] font-semibold'
                  : 'text-gray-700 border border-gray-300'
              }`}
              data-testid={`mobile-filter-${f.key}`}
            >
              {f.label}
              {badge > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
        {/* Session-49: Enquiry sub-chip REMOVED from Support panel.
            Enquiry has its own top-level slide (4th tab) so this used to
            duplicate the filter and leak inquiry chats under Support. */}
      </div>
      )}

      {/* CHAT LIST with horizontal swipe */}
      <div
        ref={listRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="flex-1 overflow-y-auto"
        data-testid="mobile-chat-list"
      >
        {isInquirySection ? (
          <InquiryPanel user={user} onOpenChat={(rid) => handleChatClick(rid)} />
        ) : (<>
        {/* Archived box (hidden when already inside archive view) */}
        {archivedCount > 0 && filterTab !== 'unread' && activeTab !== 'archive' && (
          <div
            className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-100"
            onClick={() => {
              try { sessionStorage.setItem('admin_tab_before_archive', activeTab); } catch (_) {}
              saveTabScroll();
              setActiveTab('archive');
            }}
            data-testid="mobile-archived-box"
          >
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xl">📦</span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-800">Archived</div>
              <div className="text-xs text-gray-500 mt-0.5">{archivedCount}</div>
            </div>
          </div>
        )}

        {chatList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ChatCircleDots size={48} weight="light" />
            <p className="mt-3 text-sm">No chats yet</p>
          </div>
        ) : (
          chatList.map((room) => {
            const name = room.customer_name || room.customer_phone || 'Chat';
            const paletteIdx = (name.charCodeAt(0) || 0) % AVATAR_PALETTE.length;
            const pal = AVATAR_PALETTE[paletteIdx];
            const initial = (name.trim()[0] || 'C').toUpperCase();
            const unread = unreadCounts[room.id] || room.unread_count || 0;
            const time = formatChatTime(room.last_message_time || room.updated_at);
            const preview = truncatePreview(room.last_message || '');
            const profilePic = room.customer_profile_pic;
            const picUrl = profilePic
              ? (profilePic.startsWith('http') ? profilePic : `${API}${profilePic}`)
              : null;

            const isSelected = selectionMode && selectedRooms?.includes(room.id);
            return (
              <div
                key={room.id}
                onClick={() => selectionMode ? toggleRoomSelection(room.id) : _openChat(room.id)}
                onTouchStart={(e) => !selectionMode && handleLongPressStart && handleLongPressStart(room.id, e)}
                onTouchEnd={handleLongPressEnd}
                onTouchMove={handleTouchMove}
                onMouseDown={(e) => !selectionMode && handleLongPressStart && handleLongPressStart(room.id, e)}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors ${
                  isSelected ? 'bg-[#d9fdd3]/40' : 'hover:bg-gray-50 active:bg-gray-100'
                }`}
                style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
                data-testid={`mobile-chat-row-${room.id}`}
              >
                {/* Avatar — pastel bg + darker initial (WhatsApp-style) */}
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-[19px] font-semibold"
                  style={picUrl ? {
                    backgroundImage: `url(${picUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : { backgroundColor: pal.bg, color: pal.fg }}
                >
                  {!picUrl && initial}
                </div>
                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <div className="font-semibold text-[16px] text-gray-900 truncate">{truncateName(name)}</div>
                      <MBSetupBadge value={room.mb_setup} size="xs" />
                    </div>
                    <div className={`text-[11px] flex-shrink-0 ${unread > 0 ? 'text-[#00a884] font-semibold' : 'text-gray-500'}`}>{time}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-[13.5px] text-gray-500 truncate flex-1 min-w-0">{preview}</p>
                    {unread > 0 && (
                      <span className="bg-[#00a884] text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center flex-shrink-0">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        </>)}
      </div>

      {/* Delete Confirmation Modal — rendered inside the mobile view because
          the desktop dashboard's return is skipped when this view is active. */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mobile-admin-delete-confirm">
            <h3 className="text-base font-bold mb-2 text-gray-800">Delete Chats?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Permanently delete <b>{selectedRooms.length}</b> chat{selectedRooms.length > 1 ? 's' : ''}? Messages will be moved to the recycle bin and can be restored later by admin.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                data-testid="mobile-admin-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold"
                data-testid="mobile-admin-delete-confirm-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileWhatsAppAdminView;
